import React, { useEffect, useMemo } from 'react'

import { useTranslation } from '@disco/i18n/use-translation'
import { clientLibraryListGetAll } from '@disco/state-management/actions/client-library-list-actions'
import { getArtistProfile } from '@disco/state-management/selectors/artist-profile'

import DocumentHeadTags from 'components/document-head-tags'
import { Separator, Spinner } from 'components/ds'
import ErrorPage from 'components/error-page'
import LibraryPublishActions from 'components/libraries/library-publish-actions'
import NotFoundPage from 'components/not-found-page'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'

import { useLiveLibrariesEnabled } from 'hooks/use-live-libraries-enabled'
import { useDispatch, useSelector } from 'hooks/use-redux'

import TopTracks from './top-tracks'
import ArtistProfileContact from './artist-profile-contact'

const ArtistProfile = () => {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()
  const loaded = useSelector(state => state.clientLibraries.libraries.loaded)
  const loading = useSelector(state => state.clientLibraries.libraries.loading)
  const error = useSelector(state => state.clientLibraries.libraries.error)
  const artistProfile = useSelector(getArtistProfile)
  const liveLibrariesEnabled = useLiveLibrariesEnabled()

  useEffect(() => {
    if (!loaded && !loading) {
      dispatch(clientLibraryListGetAll({}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const contactDetailsFields = useMemo(
    () => ({
      description: artistProfile?.description,
      contact_details: artistProfile?.contact_details,
      contact_website: artistProfile?.contact_website,
      display_name: artistProfile?.display_name,
    }),
    [
      artistProfile?.description,
      artistProfile?.contact_details,
      artistProfile?.contact_website,
      artistProfile?.display_name,
    ],
  )

  if (error && !loading && !loaded) {
    return <ErrorPage />
  }
  if (!loaded) {
    return <Spinner size="3" data-testid="artist-profile-spinner" />
  }

  if (!artistProfile) {
    return <NotFoundPage />
  }

  return (
    <div className="Page">
      <DocumentHeadTags
        title={t('common:artistProfileDisco', 'Artist Profile | DISCO')}
      />
      <div className="Page_Content">
        <PageHeader
          title={t('common:artistProfile', 'Artist Profile')}
          actions={
            <LibraryPublishActions
              libraryId={artistProfile.id}
              library={artistProfile}
              publishEnabled={liveLibrariesEnabled}
              viewCaption={t('common:viewProfile', 'View profile')}
              qrFilename={`${artistProfile.name || 'artist-profile'}-qr`}
              successMessage={t('common:profileUpdated', 'Profile updated!')}
            />
          }
        />
        <PageContainer>
          <PageContainer.Section
            title={t('common:manageArtistDetails', 'Manage artist details')}
          >
            <h2>{artistProfile.name}</h2>
          </PageContainer.Section>
          <Separator size="4" my="6" />
          <TopTracks libraryId={artistProfile.id} />
          <Separator size="4" my="6" />
          <ArtistProfileContact
            loading={loading}
            id={artistProfile.id}
            formFields={contactDetailsFields}
          />
          <PageContainer.Section
            title={t('common:manageTopTracks', 'Manage top tracks')}
          />
          <PageContainer.Section
            title={t('common:managePlaylists', 'Manage playlists')}
          />
          <Separator size="4" my="6" />
          <PageContainer.Section
            title={t('common:manageVideos', 'Manage videos')}
          />
        </PageContainer>
      </div>
    </div>
  )
}

export default ArtistProfile
